import styled, { css } from "styled-components/macro";
import { Link } from "react-router-dom";

import { Action } from "shared/components/Action/Action";
import {
  colors,
  boxShadow,
  fontSize,
  fontStyle,
  space,
  borderRadius,
  transition,
  visuallyHidden,
} from "shared/styles/constants";
import { ITheme } from "shared/types";

export const StyledActionDropdownContainer = styled.div`
  position: relative;

  display: inline-flex;

  @media print {
    display: none;
  }
`;

interface StyledActionDropdownTriggerProps {
  isOpen?: boolean;
}

export const StyledActionDropdownTrigger = styled(
  Action
)<StyledActionDropdownTriggerProps>`
  svg:not(:first-child) {
    color: ${colors.grey4};

    transition: transform ${transition.default};

    ${({ isOpen }) =>
      isOpen &&
      css`
        transform: rotate(180deg);
      `}
  }
`;

export interface StyledActionDropdownProps {
  noWrap?: boolean;
  overflow?: "hidden" | "visible";
  overflowY?: "auto";
  maxHeight?: string;
  minWidth?: string;
}

export const StyledActionDropdown = styled.div<StyledActionDropdownProps>`
  display: flex;
  flex-direction: column;

  max-width: ${({ noWrap }) => !noWrap && "150px"};

  padding: 0;

  background-color: ${colors.grey1};
  box-shadow: ${boxShadow.l};
  border-radius: ${borderRadius.s};

  overflow: ${({ overflow }) => overflow || "hidden"};
  overflow-y: ${({ overflowY }) => overflowY || "hidden"};
  max-height: ${({ maxHeight }) => maxHeight || "auto"};
  min-width: ${({ minWidth }) => minWidth || "auto"};
`;

export interface StyledActionDropdownItemProps {
  wrapOptions?: boolean;
  theme?: ITheme.primary | ITheme.secondary;
}

const ActionItemStyles = () => css`
  padding: calc(${space.xxs} + 2px) ${space.s};

  color: inherit;
  text-align: inherit;
  text-decoration: none;

  background-color: ${({ theme }) =>
    theme === ITheme.secondary && colors.grey2};

  > * + * {
    margin-left: ${space.xs};
  }
`;

export const StyledActionDropdownItem = styled.button<StyledActionDropdownItemProps>`
  ${ActionItemStyles}

  ${fontSize.xs}
  ${fontStyle.bold}

  &:hover,
  &:focus {
    outline: none;
    background-color: ${colors.grey2};
  }

  white-space: ${({ wrapOptions }) => (wrapOptions ? "normal" : "nowrap")};

  &:first-of-type {
    padding-top: ${space.s};
  }

  &:nth-last-of-type(2) {
    padding-bottom: ${space.s};
  }
`;

export const StyledActionDropdownLink = styled(
  Link
)<StyledActionDropdownItemProps>`
  ${ActionItemStyles}

  ${fontSize.xs}
  ${fontStyle.bold}

  &:hover,
  &:focus {
    outline: none;
    background-color: ${colors.grey2};
  }

  white-space: ${({ wrapOptions }) => (wrapOptions ? "normal" : "nowrap")};

  &:first-of-type {
    padding-top: ${space.s};
  }

  &:nth-last-of-type(2) {
    padding-bottom: ${space.s};
  }
`;

export const StyledActionDropdownCloseButton = styled(StyledActionDropdownItem)`
  &:not(:focus) {
    ${visuallyHidden};
  }
`;

export const StyledActionDropdownLabel = styled.span<StyledActionDropdownItemProps>`
  ${ActionItemStyles}

  ${fontSize.xxs}
  color: ${colors.grey5};

  padding-top: ${space.xs};
  margin-top: ${space.xs};
`;
